// src/components/CaseCreator.jsx
// ─────────────────────────────────────────────────────────────────────────────
// CASE CREATOR — Refonte cohérence visuelle avec ParcoursCreator
// Tout le code métier est INTACT.
// Seule la présentation est alignée sur le design system commun.
// ─────────────────────────────────────────────────────────────────────────────

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Activity,
  AlertTriangle,
  ArrowLeft, ArrowRight,
  BrainCircuit,
  Check,
  ChevronDown, ChevronRight,
  Circle,
  Clock,
  Cpu,
  Database, GitBranch,
  Info,
  Layers,
  Loader2,
  Menu,
  Network,
  Plus,
  Power,
  Settings,
  Sparkles,
  Target,
  Trash2,
  Users,
  X,
  Zap,
  ZapOff
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useCaseCreator } from '../stores/useCaseCreator';
import { templates } from '../templates';
import { genericCaseSchema } from '../utils/genericCaseSchema';

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — identiques à ParcoursCreator
// ─────────────────────────────────────────────────────────────────────────────

const tokens = (dm) => ({
  bg: dm ? 'bg-zinc-950' : 'bg-white',
  bgSub: dm ? 'bg-zinc-900/60' : 'bg-zinc-50',
  border: dm ? 'border-white/8' : 'border-zinc-200',
  text: dm ? 'text-zinc-100' : 'text-zinc-900',
  textSub: dm ? 'text-zinc-400' : 'text-zinc-500',
  textMuted: dm ? 'text-zinc-600' : 'text-zinc-400',
  input: dm
    ? 'bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-400/60 focus:ring-1 focus:ring-indigo-400/20'
    : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 shadow-sm',
  accent: 'text-indigo-500',
  accentBg: dm ? 'bg-indigo-500/10' : 'bg-indigo-50',
  accentBorder: dm ? 'border-indigo-500/25' : 'border-indigo-200',
});

// ─────────────────────────────────────────────────────────────────────────────
// StatusRing — identique ParcoursCreator
// ─────────────────────────────────────────────────────────────────────────────

function StatusRing({ status, size = 20 }) {
  const configs = {
    void: { color: 'text-zinc-500', bg: 'bg-transparent', border: 'border-zinc-600', icon: null },
    partial: { color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/50', icon: <Clock size={10} /> },
    complete: { color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/50', icon: <Check size={10} strokeWidth={3} /> },
  };
  const cfg = configs[status] || configs.void;
  return (
    <div className={`flex items-center justify-center rounded-full border ${cfg.bg} ${cfg.border}`} style={{ width: size, height: size }}>
      <span className={cfg.color}>{cfg.icon || <Circle size={8} />}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FieldLabel — identique ParcoursCreator
// ─────────────────────────────────────────────────────────────────────────────

function FieldLabel({ label, hint, required, darkMode }) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center gap-1.5">
        <label className={`text-[11px] font-bold uppercase tracking-widest ${darkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
          {label}
        </label>
        {required && (
          <span className="text-[9px] font-bold text-red-400 border border-red-400/30 px-1 rounded-sm">requis</span>
        )}
      </div>
      {hint && (
        <p className={`text-[11px] mt-0.5 leading-snug ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{hint}</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WorkspaceCard — identique ParcoursCreator, accent indigo par défaut
// ─────────────────────────────────────────────────────────────────────────────

function WorkspaceCard({ title, subtitle, icon: Icon, accent = 'indigo', required = false, children, darkMode, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  const accentColors = {
    indigo: { border: 'border-l-indigo-400', icon: 'text-indigo-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    emerald: { border: 'border-l-emerald-400', icon: 'text-emerald-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    red: { border: 'border-l-red-400', icon: 'text-red-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    purple: { border: 'border-l-purple-400', icon: 'text-purple-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    amber: { border: 'border-l-amber-400', icon: 'text-amber-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    sky: { border: 'border-l-sky-400', icon: 'text-sky-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
  };
  const ac = accentColors[accent] || accentColors.indigo;

  return (
    <div className={`rounded-lg border transition-all overflow-y-auto no-scrollbar ${
      open
        ? `${ac.border} border-l-2 border-y  ${darkMode ? 'border-white/8 bg-zinc-900/40' : 'border-zinc-200 bg-white shadow-sm'}`
        : `border-l-2 border-l-transparent border-y  ${darkMode ? 'border-white/5' : 'border-zinc-100'}`
    }`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between px-5 py-4 transition-colors ${darkMode ? 'hover:bg-white/3' : 'hover:bg-zinc-50/70'}`}
      >
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 p-1.5 rounded-md ${darkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
            <Icon size={14} className={open ? ac.icon : darkMode ? 'text-zinc-500' : 'text-zinc-400'} />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className={`text-[13px] font-semibold ${open ? ac.title : darkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{title}</span>
              {required && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${open ? 'text-red-400 border-red-400/30 bg-red-400/5' : 'text-zinc-500 border-zinc-600'}`}>REQUIS</span>
              )}
            </div>
            {subtitle && open && <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{subtitle}</p>}
          </div>
        </div>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${darkMode ? 'text-zinc-600' : 'text-zinc-400'} ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`px-5 pb-5  ${darkMode ? 'border-white/5' : 'border-zinc-100'}`}>
          <div className="pt-4">{children}</div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WorkspaceContextBar — identique ParcoursCreator
// ─────────────────────────────────────────────────────────────────────────────

function WorkspaceContextBar({ protocolLabel, protocolHint, darkMode }) {
  return (
    <div className={`px-6 py-3  flex items-center justify-between ${darkMode ? 'bg-zinc-900/50 border-white/6' : 'bg-zinc-50/80 border-zinc-200'}`}>
      <div className="flex items-center gap-2">
        <span className={`text-[11px] font-medium ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>Constructeur</span>
        <ChevronRight size={10} className={darkMode ? 'text-zinc-700' : 'text-zinc-300'} />
        <span className={`text-[12px] font-semibold ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>{protocolLabel}</span>
      </div>
      {protocolHint && (
        <div className={`hidden md:flex items-center gap-1.5 text-[11px] ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
          <Info size={11} className="text-indigo-400/60 flex-shrink-0" />
          <span>{protocolHint}</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MissionControl sidebar — style ParcoursCreator, labels CaseCreator
// ─────────────────────────────────────────────────────────────────────────────

function MissionControl({ protocols, currentProtocol, setProtocol, darkMode, interfaceMode }) {
  const dm = darkMode;
  const isSimple = interfaceMode === 'simplifie';

  const protocolGuide = {
    1: { emoji: '🏷️', what: 'Nommez votre simulation et définissez ses valeurs', why: 'La base de tout le scénario', tip: 'Un bon slug est court et sans espaces.' },
    2: { emoji: '👥', what: 'Créez les groupes et personnages du scénario', why: 'Ils incarnent les tensions sociales', tip: 'Commencez par 2-3 familles et autant d\'agents.' },
    3: { emoji: '⏱️', what: 'Structurez la progression temporelle', why: '7 segments minimum pour une simulation valide', tip: 'Utilisez l\'injection automatique pour démarrer.' },
    4: { emoji: '⚙️', what: 'Calibrez le moteur d\'inférence', why: 'Détermine comment les variables évoluent', tip: 'Le mode Bayésien est recommandé pour débuter.' },
    5: { emoji: '🏁', what: 'Générez les issues et le débrief', why: 'La conclusion pédagogique du scénario', tip: 'Choisissez un ton narratif cohérent avec votre contexte.' },
  };

  const guide = protocolGuide[currentProtocol];
  const currentP = protocols.find(p => p.id === currentProtocol);

  return (
    <div className={`flex flex-col h-full ${dm ? 'bg-zinc-950' : 'bg-white'}`}>
      {/* Logo */}
      <div className={`p-5  ${dm ? 'border-white/6' : 'border-zinc-100'}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <Cpu size={14} className="text-white" />
          </div>
          <div>
            <div className={`text-[12px] font-bold ${dm ? 'text-white' : 'text-zinc-900'}`}>Constructeur PESA</div>
            <div className={`text-[10px] ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>
              {isSimple ? 'Assistant guidé' : 'Architecte de simulation'}
            </div>
          </div>
        </div>
      </div>

      {/* Barre de progression */}
      <div className={`px-5 py-4  ${dm ? 'border-white/6' : 'border-zinc-100'}`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[11px] font-medium ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>Avancement</span>
          <span className={`text-[12px] font-bold ${dm ? 'text-indigo-400' : 'text-indigo-600'}`}>{currentProtocol}/5</span>
        </div>
        <div className={`h-2 rounded-full overflow-y-auto no-scrollbar ${dm ? 'bg-zinc-800' : 'bg-zinc-200'}`}>
          <div
            className="h-full rounded-full bg-indigo-500 transition-all duration-700"
            style={{ width: `${(currentProtocol / 5) * 100}%` }}
          />
        </div>
      </div>

      {/* Protocoles */}
      <div className="px-3 py-4 flex-1 overflow-y-auto">
        <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 px-2 ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>Étapes</p>

        <div className="space-y-1">
          {protocols.map((p) => {
            const isActive = currentProtocol === p.id;
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                onClick={() => setProtocol(p.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-left ${
                  isActive
                    ? dm ? 'bg-indigo-500/12 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200'
                    : dm ? 'hover:bg-zinc-900 border border-transparent' : 'hover:bg-zinc-50 border border-transparent'
                }`}
              >
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isActive
                    ? 'bg-indigo-500 text-white'
                    : dm ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-100 text-zinc-400'
                }`}>
                  <Icon size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-[12px] font-semibold ${isActive ? dm ? 'text-indigo-300' : 'text-indigo-700' : dm ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    {isSimple ? p.labelSimple : p.labelExpert}
                  </span>
                  <p className={`text-[10px] mt-0.5 truncate ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>{p.hint}</p>
                </div>
                {isActive && <ChevronRight size={12} className="text-indigo-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Guide contextuel */}
        {guide && (
          <div className={`mt-5 mx-1 p-4 rounded-xl border ${dm ? 'border-indigo-500/15 bg-indigo-500/5' : 'border-indigo-100 bg-indigo-50/50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">{guide.emoji}</span>
              <span className={`text-[11px] font-semibold ${dm ? 'text-indigo-300' : 'text-indigo-700'}`}>{currentP?.labelSimple}</span>
            </div>
            <p className={`text-[11px] font-medium mb-1 ${dm ? 'text-zinc-300' : 'text-zinc-700'}`}>{guide.what}</p>
            <p className={`text-[11px] mb-2 ${dm ? 'text-zinc-500' : 'text-zinc-500'}`}>{guide.why}</p>
            <div className={`pt-2  text-[10px] italic ${dm ? 'border-white/5 text-zinc-600' : 'border-indigo-100 text-zinc-400'}`}>
              💡 {guide.tip}
            </div>
          </div>
        )}
      </div>

      {/* Footer mode */}
      <div className={`p-4  ${dm ? 'border-white/6' : 'border-zinc-100'}`}>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${dm ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className={`text-[11px] font-medium ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>
            {isSimple ? 'Mode Simplifié actif' : 'Mode Expert actif'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TOUS LES SOUS-COMPOSANTS MÉTIER — intacts
// ─────────────────────────────────────────────────────────────────────────────

const PhaseEditor = ({ stepIndex, pIdx, register, control, removePhase, darkMode }) => {
  const { fields: microFields, append: appendMicro, remove: removeMicro } = useFieldArray({
    control,
    name: `steps.${stepIndex}.phases.${pIdx}.microEffects`,
  });

  return (
    <div className="border border-emerald-500/20 bg-emerald-950/10 p-5 rounded-md space-y-5 relative group/phase">
      <div className="flex items-center justify-between  border-emerald-500/30 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
          <span className="text-[11px] font-black font-mono text-emerald-400 uppercase tracking-widest">Phase_{pIdx + 1}</span>
        </div>
        <button type="button" onClick={() => removePhase(pIdx)} className="opacity-0 group-hover/phase:opacity-100 text-red-500 hover:text-red-400 text-xs font-black transition-opacity">× REMOVE_PHASE</button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4">
          <label className="text-[10px] font-mono text-emerald-500/60 uppercase tracking-widest block mb-1">Phase_Type</label>
          <select {...register(`steps.${stepIndex}.phases.${pIdx}.type`)} className="w-full bg-black/60 border border-emerald-500/20 p-2 text-xs font-mono text-emerald-300 outline-none focus:border-emerald-500">
            <option value="observation">OBSERVATION</option>
            <option value="action">ACTION</option>
            <option value="consequences">CONSEQUENCES</option>
            <option value="tension">TENSION</option>
            <option value="bifurcation">BIFURCATION</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-[10px] font-mono text-emerald-500/60 uppercase tracking-widest block mb-1">Duration (s)</label>
          <input type="number" {...register(`steps.${stepIndex}.phases.${pIdx}.duration`, { valueAsNumber: true })} className="w-full bg-black/60 border border-emerald-500/20 p-2 text-xs font-mono text-center text-emerald-300 outline-none focus:border-emerald-500" min="1" placeholder="15" />
        </div>
        <div className="col-span-6">
          <label className="text-[10px] font-mono text-emerald-500/60 uppercase tracking-widest block mb-1">Phase_Description</label>
          <input {...register(`steps.${stepIndex}.phases.${pIdx}.description`)} className="w-full bg-black/60 border border-emerald-500/20 p-2 text-xs font-mono text-emerald-300 outline-none focus:border-emerald-500" placeholder="Initialisation des variables système" />
        </div>
      </div>

      <div className="ml-4 mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-[10px] font-mono text-emerald-400/80 uppercase tracking-widest flex items-center gap-2"><Zap size={12} /> Phase_Micro_Delta_Processor</label>
          <button type="button" onClick={() => appendMicro({ target: '', variable: '', delta: 0, temporalWeight: 1.0 })} className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors">+ Add_Micro_Effect</button>
        </div>
        <div className="space-y-2">
          {microFields.map((micro, mIdx) => (
            <div key={micro.id} className="grid grid-cols-12 gap-2 bg-black/40 border border-emerald-500/10 p-2 rounded-sm group/micro">
              <input {...register(`steps.${stepIndex}.phases.${pIdx}.microEffects.${mIdx}.target`)} placeholder="Target (ex: Élève_1)" className="col-span-4 bg-transparent border border-white/10 p-1.5 text-[11px] font-mono text-emerald-300 outline-none" />
              <input {...register(`steps.${stepIndex}.phases.${pIdx}.microEffects.${mIdx}.variable`)} placeholder="Variable (ex: bienEtre)" className="col-span-3 bg-transparent border border-white/10 p-1.5 text-[11px] font-mono text-emerald-300 outline-none" />
              <input type="number" {...register(`steps.${stepIndex}.phases.${pIdx}.microEffects.${mIdx}.delta`, { valueAsNumber: true })} placeholder="Δ" className="col-span-2 bg-transparent border border-white/10 p-1.5 text-[11px] font-mono text-center text-emerald-300 outline-none" />
              <input type="number" step="0.1" {...register(`steps.${stepIndex}.phases.${pIdx}.microEffects.${mIdx}.temporalWeight`, { valueAsNumber: true })} placeholder="Temporal W" className="col-span-2 bg-transparent border border-white/10 p-1.5 text-[11px] font-mono text-center text-emerald-300 outline-none" />
              <button type="button" onClick={() => removeMicro(mIdx)} className="col-span-1 opacity-0 group-hover/micro:opacity-100 text-red-500 hover:text-red-400 text-xs transition-opacity">×</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StepEditor = ({ index, register, control, removeStep, expandedSteps, darkMode, toggleExpanded }) => {
  const { fields: causalityFields, append: appendCausality, remove: removeCausality } = useFieldArray({ control, name: `steps.${index}.causality` });
  const { fields: phasesFields, append: appendPhase, remove: removePhase } = useFieldArray({ control, name: `steps.${index}.phases` });
  const { fields: actionsFields, append: appendAction, remove: removeAction } = useFieldArray({ control, name: `steps.${index}.actions` });
  const { fields: autoEventsFields, append: appendAutoEvent, remove: removeAutoEvent } = useFieldArray({ control, name: `steps.${index}.autoEvents` });

  const dm = darkMode;

  return (
    <div className="relative mb-20 group animate-in fade-in duration-500">
      <div className="absolute -left-[41px] top-0 bottom-[-80px] w-px bg-gradient-to-b from-red-600 via-red-900/20 to-transparent" />
      <div className="absolute -left-[41px] top-4 w-4 h-4 rounded-full border-2 border-red-600 bg-black z-10 shadow-[0_0_15px_rgba(220,38,38,0.4)]" />

      <div className={`border rounded-lg overflow-y-auto no-scrollbar shadow-2xl ${dm ? 'bg-zinc-950 border-white/8' : 'bg-white border-zinc-200'}`}>
        {/* TOP BAR */}
        <div className={`flex items-center justify-between px-5 py-3  ${dm ? 'bg-red-600/5 border-white/8' : 'bg-red-50 border-zinc-200'}`}>
          <div className="flex items-center gap-3">
            <span className={`text-[12px] font-bold tracking-widest uppercase ${dm ? 'text-red-400' : 'text-red-600'}`}>Segment {index + 1}</span>
            <div className={`h-1.5 w-10 rounded-full overflow-y-auto no-scrollbar ${dm ? 'bg-red-900/30' : 'bg-red-200'}`}>
              <div className="h-full bg-red-500 w-2/3 animate-pulse" />
            </div>
          </div>
          <button type="button" onClick={() => removeStep(index)} className={`p-1.5 rounded-md transition-colors ${dm ? 'text-zinc-600 hover:text-red-400 hover:bg-red-500/10' : 'text-zinc-400 hover:text-red-500 hover:bg-red-50'}`}>
            <Trash2 size={14} />
          </button>
        </div>

        {/* SECTION CORE NARRATIVE */}
        <div className="p-6 space-y-5">
          <input
            {...register(`steps.${index}.title`)}
            placeholder="Titre du segment..."
            className={`w-full bg-transparent text-2xl font-bold tracking-tight outline-none -2 pb-2 transition-colors ${dm ? 'text-white border-zinc-800 focus:border-red-500/50 placeholder:text-zinc-700' : 'text-zinc-900 border-zinc-200 focus:border-red-400 placeholder:text-zinc-300'}`}
          />

          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-8 space-y-3">
              <div>
                <FieldLabel label="Description narrative" darkMode={dm} />
                <textarea
                  {...register(`steps.${index}.description`)}
                  className={`w-full p-3 text-[13px] font-mono border rounded-lg resize-none outline-none ${dm ? 'bg-zinc-900/60 border-zinc-700 text-zinc-300 focus:border-red-500/40' : 'bg-zinc-50 border-zinc-200 text-zinc-700 focus:border-red-300'}`}
                  rows={3}
                  placeholder="Décrivez ce qui se passe dans ce segment..."
                />
              </div>
            </div>
            <div className={`col-span-4 p-4 rounded-lg border space-y-3 ${dm ? 'bg-zinc-900/40 border-zinc-800' : 'bg-zinc-50 border-zinc-200'}`}>
              <div>
                <label className={`text-[10px] font-semibold uppercase tracking-widest block mb-1 ${dm ? 'text-red-400/70' : 'text-red-600/70'}`}>Problématisation</label>
                <textarea {...register(`steps.${index}.problematisation`)} className={`w-full p-2 text-[11px] font-mono border rounded-md resize-none outline-none ${dm ? 'bg-black/40 border-zinc-700 text-zinc-400' : 'bg-white border-zinc-200 text-zinc-500'}`} rows={2} />
              </div>
              <div>
                <label className={`text-[10px] font-semibold uppercase tracking-widest block mb-1 ${dm ? 'text-red-400/70' : 'text-red-600/70'}`}>Question épistémologique</label>
                <textarea {...register(`steps.${index}.epistemologicalQuestion`)} className={`w-full p-2 text-[11px] font-mono border rounded-md resize-none outline-none ${dm ? 'bg-black/40 border-zinc-700 text-zinc-400' : 'bg-white border-zinc-200 text-zinc-500'}`} rows={2} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel label="Narrative Override" darkMode={dm} />
              <input {...register(`steps.${index}.narrativeOverride`)} className={`w-full p-2.5 text-[12px] font-mono border rounded-lg outline-none ${dm ? 'bg-zinc-900 border-indigo-500/20 text-indigo-300 focus:border-indigo-500/50' : 'bg-white border-indigo-200 text-indigo-700 focus:border-indigo-400'}`} placeholder="LE VIVANT COMMENCE À BOUGER" />
            </div>
            <div>
              <FieldLabel label="Feedback global" darkMode={dm} />
              <input {...register(`steps.${index}.feedback`)} className={`w-full p-2.5 text-[12px] font-mono border rounded-lg outline-none ${dm ? 'bg-zinc-900 border-emerald-500/20 text-emerald-300 focus:border-emerald-500/50' : 'bg-white border-emerald-200 text-emerald-700 focus:border-emerald-400'}`} placeholder="Le réseau commence à respirer autrement." />
            </div>
          </div>
        </div>

        {/* TOGGLE SUB-MODULES */}
        <button
          type="button"
          onClick={() => toggleExpanded(index)}
          className={`w-full py-3  flex items-center justify-center gap-3 group/btn transition-all ${dm ? 'bg-white/3 border-white/8 hover:bg-red-500/8' : 'bg-zinc-50 border-zinc-200 hover:bg-red-50'}`}
        >
          <Cpu size={13} className={`text-red-500 transition-transform ${expandedSteps[index] ? 'rotate-90' : ''}`} />
          <span className={`text-[11px] font-semibold uppercase tracking-widest ${dm ? 'text-zinc-500' : 'text-zinc-500'}`}>
            {expandedSteps[index] ? 'Fermer les modules avancés' : 'Configurer causalité, phases, actions'}
          </span>
          <ChevronDown size={13} className={`transition-transform ${dm ? 'text-zinc-600' : 'text-zinc-400'} ${expandedSteps[index] ? 'rotate-180' : ''}`} />
        </button>

        {expandedSteps[index] && (
          <div className={`p-8 space-y-12 animate-in slide-in-from-top-2 duration-300 ${dm ? 'bg-zinc-950/80' : 'bg-zinc-50'}`}>

            {/* MODULE CAUSALITÉ */}
            <div className="space-y-4">
              <div className="flex justify-between items-end  border-blue-500/30 pb-2">
                <h4 className="text-[11px] font-bold font-mono text-blue-500 uppercase tracking-widest flex items-center gap-2"><GitBranch size={14} /> Matrice Causale</h4>
                <button type="button" onClick={() => appendCausality({ cause: '', effect: '', weight: 1, type: 'positive', evidence: { source: 'ENGINE', strength: 0.9 } })} className="text-[11px] font-mono text-blue-400 border border-blue-500/20 px-3 py-1 rounded-md hover:bg-blue-500/10 transition-colors">+ Nœud causal</button>
              </div>
              <div className="grid grid-cols-1 gap-1">
                {causalityFields.map((field, cIdx) => (
                  <div key={field.id} className="grid grid-cols-12 gap-1 h-8">
                    <input {...register(`steps.${index}.causality.${cIdx}.cause`)} className={`col-span-3 px-2 text-[12px] font-mono outline-none rounded ${dm ? 'bg-white/5 text-zinc-300' : 'bg-zinc-100 text-zinc-700'}`} placeholder="CAUSE" />
                    <input {...register(`steps.${index}.causality.${cIdx}.effect`)} className={`col-span-3 px-2 text-[12px] font-mono outline-none rounded ${dm ? 'bg-white/5 text-blue-300' : 'bg-zinc-100 text-blue-700'}`} placeholder="EFFECT" />
                    <input type="number" step="0.1" {...register(`steps.${index}.causality.${cIdx}.weight`, { valueAsNumber: true })} className={`col-span-1 text-[12px] font-mono text-center outline-none rounded ${dm ? 'bg-white/5' : 'bg-zinc-100'}`} placeholder="W" />
                    <select {...register(`steps.${index}.causality.${cIdx}.type`)} className={`col-span-2 text-[12px] font-mono outline-none rounded ${dm ? 'bg-white/5' : 'bg-zinc-100'}`}>
                      <option value="positive">positive</option>
                      <option value="negative">negative</option>
                    </select>
                    <input {...register(`steps.${index}.causality.${cIdx}.evidence.source`)} className={`col-span-2 px-2 text-[11px] font-mono opacity-40 outline-none rounded ${dm ? 'bg-white/5' : 'bg-zinc-100'}`} placeholder="SOURCE" />
                    <input type="number" step="0.01" {...register(`steps.${index}.causality.${cIdx}.evidence.strength`, { valueAsNumber: true })} className={`col-span-1 text-[11px] font-mono text-center outline-none rounded ${dm ? 'bg-white/5' : 'bg-zinc-100'}`} placeholder="STR" />
                    <button type="button" onClick={() => removeCausality(cIdx)} className="col-span-1 text-red-500 text-xs hover:text-red-400 transition-colors rounded">×</button>
                  </div>
                ))}
              </div>
            </div>

            {/* MODULE PHASES */}
            <div className="space-y-4">
              <div className="flex justify-between items-end  border-emerald-500/30 pb-2">
                <h4 className="text-[11px] font-bold font-mono text-emerald-500 uppercase tracking-widest flex items-center gap-2"><Activity size={14} /> Timeline</h4>
                <button type="button" onClick={() => appendPhase({ type: 'observation', duration: 15, description: '' })} className="text-[11px] font-mono text-emerald-400 border border-emerald-500/20 px-3 py-1 rounded-md hover:bg-emerald-500/10 transition-colors">+ Phase</button>
              </div>
              <div className="space-y-6">
                {phasesFields.map((field, pIdx) => (
                  <PhaseEditor key={field.id} stepIndex={index} pIdx={pIdx} register={register} control={control} removePhase={removePhase} darkMode={dm} />
                ))}
              </div>
            </div>

            {/* MODULE ACTIONS */}
            <div className="space-y-4">
              <div className="flex justify-between items-end  border-amber-500/30 pb-2">
                <h4 className="text-[11px] font-bold font-mono text-amber-500 uppercase tracking-widest flex items-center gap-2"><Zap size={14} /> Actions utilisateur</h4>
                <button type="button" onClick={() => appendAction({ id: `action_${Date.now().toString(36)}`, label: 'Nouvelle action', effect: {}, prerequisites: [], addInteractions: [], toggleFamilies: [], highlightFamilies: [], feedback: '', microEffects: [] })} className="text-[11px] font-mono text-amber-400 border border-amber-500/20 px-3 py-1 rounded-md hover:bg-amber-500/10 transition-colors">+ Action</button>
              </div>
              <div className="grid grid-cols-1 gap-5">
                {actionsFields.map((field, aIdx) => (
                  <div key={field.id} className={`border rounded-lg p-5 space-y-4 relative group/act ${dm ? 'bg-zinc-900/40 border-white/8' : 'bg-white border-zinc-200 shadow-sm'}`}>
                    <div className="flex justify-between items-center  border-white/10 pb-3">
                      <input {...register(`steps.${index}.actions.${aIdx}.id`)} className="bg-transparent text-[11px] font-mono text-amber-500/60 outline-none w-1/3" placeholder="ACTION_ID" />
                      <button type="button" onClick={() => removeAction(aIdx)} className={`opacity-0 group-hover/act:opacity-100 text-[11px] font-medium transition-opacity ${dm ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-600'}`}>Supprimer</button>
                    </div>
                    <input {...register(`steps.${index}.actions.${aIdx}.label`)} className={`w-full p-3 text-[13px] font-semibold border-l-4 border-amber-500 outline-none ${dm ? 'bg-zinc-900/60 text-white' : 'bg-amber-50/50 text-zinc-900'}`} placeholder="Label de l'action..." />
                    <div>
                      <FieldLabel label="Feedback" darkMode={dm} />
                      <input {...register(`steps.${index}.actions.${aIdx}.feedback`)} className={`w-full p-2.5 text-[12px] font-mono border rounded-lg outline-none ${dm ? 'bg-zinc-900 border-amber-500/20 text-amber-300' : 'bg-white border-amber-200 text-amber-700'}`} />
                    </div>
                    <div>
                      <FieldLabel label="Effet principal (JSON)" darkMode={dm} />
                      <textarea {...register(`steps.${index}.actions.${aIdx}.effect`)} className={`w-full h-16 p-2 text-[11px] font-mono border rounded-lg outline-none resize-none ${dm ? 'bg-black/60 border-zinc-700 text-zinc-300' : 'bg-zinc-50 border-zinc-200 text-zinc-600'}`} placeholder='{"creativite": 32, "bienEtre": 22}' />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <FieldLabel label="Highlight Families" darkMode={dm} />
                        <input {...register(`steps.${index}.actions.${aIdx}.highlightFamilies`)} className={`w-full p-2.5 text-[11px] font-mono border rounded-lg outline-none ${dm ? 'bg-zinc-900 border-zinc-700 text-zinc-300' : 'bg-white border-zinc-200'}`} placeholder='["Art", "École"]' />
                      </div>
                      <div>
                        <FieldLabel label="Toggle Families" darkMode={dm} />
                        <input {...register(`steps.${index}.actions.${aIdx}.toggleFamilies`)} className={`w-full p-2.5 text-[11px] font-mono border rounded-lg outline-none ${dm ? 'bg-zinc-900 border-zinc-700 text-zinc-300' : 'bg-white border-zinc-200'}`} placeholder='["Médiation"]' />
                      </div>
                    </div>
                    <div>
                      <FieldLabel label="Prérequis" darkMode={dm} />
                      <input {...register(`steps.${index}.actions.${aIdx}.prerequisites`)} className={`w-full p-2.5 text-[11px] font-mono border rounded-lg outline-none ${dm ? 'bg-zinc-900 border-zinc-700 text-zinc-300' : 'bg-white border-zinc-200'}`} placeholder='["diag_art_absent"]' />
                    </div>
                    <div>
                      <FieldLabel label="Micro Effects (JSON)" darkMode={dm} />
                      <textarea {...register(`steps.${index}.actions.${aIdx}.microEffects`)} className={`w-full h-12 p-2 text-[11px] font-mono border rounded-lg outline-none resize-none ${dm ? 'bg-black/60 border-zinc-700 text-zinc-300' : 'bg-zinc-50 border-zinc-200'}`} />
                    </div>
                    <div>
                      <FieldLabel label="Add Interactions (JSON)" darkMode={dm} />
                      <textarea {...register(`steps.${index}.actions.${aIdx}.addInteractions`)} className={`w-full h-12 p-2 text-[11px] font-mono border rounded-lg outline-none resize-none ${dm ? 'bg-black/60 border-zinc-700 text-zinc-300' : 'bg-zinc-50 border-zinc-200'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* MODULE AUTO-EVENTS */}
            <div className="space-y-4">
              <div className="flex justify-between items-end  border-purple-500/30 pb-2">
                <h4 className="text-[11px] font-bold font-mono text-purple-500 uppercase tracking-widest flex items-center gap-2"><ZapOff size={14} /> Déclencheurs automatiques</h4>
                <button type="button" onClick={() => appendAutoEvent({ trigger: '', effect: {} })} className="text-[11px] font-mono text-purple-400 border border-purple-500/20 px-3 py-1 rounded-md hover:bg-purple-500/10 transition-colors">+ Trigger</button>
              </div>
              <div className="space-y-2">
                {autoEventsFields.map((ae, aeIdx) => (
                  <div key={ae.id} className="flex gap-2 items-center">
                    <input {...register(`steps.${index}.autoEvents.${aeIdx}.trigger`)} placeholder="TRIGGER_CONDITION" className={`flex-1 p-2 text-[11px] font-mono border rounded-lg outline-none ${dm ? 'bg-zinc-900 border-zinc-700 text-zinc-300' : 'bg-white border-zinc-200'}`} />
                    <input {...register(`steps.${index}.autoEvents.${aeIdx}.effect`)} placeholder="EFFECT_JSON" className={`flex-1 p-2 text-[11px] font-mono border rounded-lg outline-none ${dm ? 'bg-zinc-900 border-zinc-700 text-zinc-300' : 'bg-white border-zinc-200'}`} />
                    <button type="button" onClick={() => removeAutoEvent(aeIdx)} className="text-red-500 hover:text-red-400 text-sm px-2">×</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PROTOCOLES — labels enrichis de hints
// ─────────────────────────────────────────────────────────────────────────────

const PROTOCOLS = [
  { id: 1, labelSimple: 'Informations Générales', labelExpert: 'Ontologie Racine', icon: Settings, color: 'indigo', hint: 'Identité, paramètres initiaux' },
  { id: 2, labelSimple: 'Acteurs & Familles', labelExpert: 'Champs & Acteurs', icon: Users, color: 'emerald', hint: 'Groupes et personnages' },
  { id: 3, labelSimple: 'Séquence Temporelle', labelExpert: 'Séquence Narrative', icon: GitBranch, color: 'red', hint: 'Segments et étapes clés' },
  { id: 4, labelSimple: 'Configuration Moteur', labelExpert: 'Moteur d\'Inférence', icon: BrainCircuit, color: 'purple', hint: 'Calibration, causalité' },
  { id: 5, labelSimple: 'Solutions & Résultat', labelExpert: 'Solutions & Débrief', icon: Target, color: 'violet', hint: 'Issues, débrief final' },
];

// ─────────────────────────────────────────────────────────────────────────────
// PROTOCOLE 1 — refonte progressive disclosure
// ─────────────────────────────────────────────────────────────────────────────

export function Protocol1_Ontologie({ register, watch, errors, darkMode }) {
  const { interfaceMode } = useCaseCreator();
  const isSimple = interfaceMode === 'simplifie';
  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = `${tk.input} border outline-none transition-all rounded-lg px-3 py-2.5 text-[13px] w-full`;

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? 'text-white' : 'text-zinc-900'}`}>
            {isSimple ? 'Informations générales' : 'Ontologie Racine'}
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            {isSimple ? 'Les données de base de votre simulation' : 'Configuration système racine et vecteurs initiaux'}
          </p>
        </div>
        <div className={`text-[10px] font-mono px-2 py-1 rounded border ${dm ? 'border-zinc-700 text-zinc-500' : 'border-zinc-200 text-zinc-400'}`}>
          {isSimple ? 'MODE SIMPLIFIÉ' : 'MODE EXPERT'}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Colonne gauche */}
        <div className="col-span-12 lg:col-span-7 space-y-4">

          {/* Identité — REQUIS */}
          <WorkspaceCard title="Identité de la simulation" subtitle="Nom, code unique et description du scénario" icon={Database} accent="indigo" required darkMode={dm} defaultOpen>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel label={isSimple ? 'Code unique' : 'Identifiant technique'} hint="Courte chaîne sans espaces" required darkMode={dm} />
                  <input {...register('slug')} placeholder={isSimple ? 'ex: violence-symbolique' : 'SYSTEM_CORE_01'} className={inputCls} />
                  {errors?.slug?.message && <p className="text-red-400 text-[11px] mt-1">{errors.slug.message}</p>}
                </div>
                <div>
                  <FieldLabel label={isSimple ? 'Titre' : 'Label principal'} required darkMode={dm} />
                  <input {...register('title')} placeholder={isSimple ? "Violence Symbolique à l'École" : 'Entrer le label...'} className={inputCls} />
                  {errors?.title?.message && <p className="text-red-400 text-[11px] mt-1">{errors.title.message}</p>}
                </div>
              </div>
              <div>
                <FieldLabel label={isSimple ? 'Nom court' : 'Référence système'} darkMode={dm} />
                <input {...register('name')} placeholder={isSimple ? 'ex: Violence Symbolique' : 'CORE_ALPHA'} className={inputCls} />
              </div>
              <div>
                <FieldLabel label="Description" hint={isSimple ? 'Résumé visible par les apprenants' : 'Description technique du système'} required darkMode={dm} />
                <textarea {...register('description')} rows={3} placeholder={isSimple ? 'Décrivez brièvement ce que les apprenants vont découvrir...' : 'Description technique du système'} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <FieldLabel label={isSimple ? 'Objectif pédagogique' : 'Vecteur principal'} hint={isSimple ? 'Ce que les apprenants comprendront à la fin' : 'Finalité pédagogique'} darkMode={dm} />
                <textarea {...register('pedagogicalGoal')} rows={2} placeholder={isSimple ? "Qu'est-ce que les apprenants vont comprendre ?" : 'Finalité pédagogique du système'} className={`${inputCls} resize-none`} />
              </div>
            </div>
          </WorkspaceCard>

          {/* Options globales */}
          <WorkspaceCard title="Options générales" subtitle="Niveau, durée et publication" icon={Settings} accent="sky" darkMode={dm} defaultOpen={false}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel label={isSimple ? 'Niveau de difficulté' : 'Complexité'} darkMode={dm} />
                <select {...register('difficulty')} className={inputCls + ' appearance-none'}>
                  <option value="facile">Facile</option>
                  <option value="moyen">Moyen</option>
                  <option value="difficile">Difficile</option>
                  {!isSimple && <option value="expert">Expert</option>}
                </select>
              </div>
              <div>
                <FieldLabel label={isSimple ? 'Durée (minutes)' : 'Temps estimé'} darkMode={dm} />
                <input type="number" {...register('estimatedTime', { valueAsNumber: true })} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg transition-colors ${dm ? 'hover:bg-zinc-800' : 'hover:bg-zinc-50'}`}>
                  <input type="checkbox" {...register('isPublished')} className="w-4 h-4 accent-indigo-500 rounded" />
                  <div>
                    <span className={`text-[12px] font-medium ${dm ? 'text-zinc-300' : 'text-zinc-700'}`}>{isSimple ? 'Publier la simulation' : 'Prêt au déploiement'}</span>
                    {isSimple && <p className={`text-[11px] mt-0.5 ${tk.textMuted}`}>Rend la simulation accessible aux apprenants</p>}
                  </div>
                </label>
              </div>
            </div>
          </WorkspaceCard>
        </div>

        {/* Colonne droite — Métriques initiales */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          <WorkspaceCard title={isSimple ? 'Valeurs initiales' : 'Vecteurs initiaux'} subtitle="État du système au démarrage de la simulation" icon={Zap} accent="emerald" darkMode={dm} defaultOpen>
            <div className="space-y-4">
              {['stabilite', 'creativite', 'equite', 'bienEtre', 'apprentissage', 'sante', 'mediation', 'emploi'].map(param => {
                const labels = { stabilite: 'Stabilité', creativite: 'Créativité', equite: 'Équité', bienEtre: 'Bien-être', apprentissage: 'Apprentissage', sante: 'Santé', mediation: 'Médiation', emploi: 'Emploi' };
                const val = watch(`initialState.parameters.${param}`) ?? 50;
                return (
                  <div key={param} className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className={`text-[11px] font-medium ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>{isSimple ? labels[param] : param}</span>
                      <span className={`text-[11px] font-bold ${dm ? 'text-emerald-400' : 'text-emerald-600'}`}>{val}%</span>
                    </div>
                    <div className={`relative h-1.5 w-full rounded-full overflow-y-auto no-scrollbar ${dm ? 'bg-zinc-800' : 'bg-zinc-200'}`}>
                      <div className="absolute top-0 left-0 h-full bg-emerald-500 transition-all duration-300 rounded-full" style={{ width: `${val}%` }} />
                      <input type="range" {...register(`initialState.parameters.${param}`, { valueAsNumber: true })} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                    </div>
                  </div>
                );
              })}
              {isSimple && <p className={`text-[11px] italic mt-2 ${tk.textMuted}`}>Ces valeurs définissent l'état initial. Ajustez selon votre scénario.</p>}
            </div>
          </WorkspaceCard>
        </div>
      </div>

      {isSimple && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 ${dm ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
          <Info size={14} className="text-indigo-400 flex-shrink-0 mt-0.5" />
          <p className={`text-[12px] leading-relaxed ${dm ? 'text-indigo-300/80' : 'text-indigo-700'}`}>
            <strong>Conseil :</strong> Commencez par remplir le titre et la description. Les valeurs initiales peuvent être ajustées lors des tests.
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROTOCOLE 2 — refonte progressive disclosure
// ─────────────────────────────────────────────────────────────────────────────

export function Protocol2_Structure({ control, register, watch, darkMode }) {
  const { interfaceMode } = useCaseCreator();
  const isSimple = interfaceMode === 'simplifie';
  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = `${tk.input} border outline-none transition-all rounded-lg px-3 py-2 text-[12px] w-full`;

  const { fields: families, append: addFamily, remove: removeFamily } = useFieldArray({ control, name: 'initialState.families' });
  const { fields: agents, append: addAgent, remove: removeAgent } = useFieldArray({ control, name: 'initialState.agentTemplates' });

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? 'text-white' : 'text-zinc-900'}`}>
            {isSimple ? 'Acteurs & Familles' : 'Champs & Agents'}
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            {isSimple ? 'Les groupes et personnages qui interagissent dans la simulation' : 'Configuration des champs sociaux et agents'}
          </p>
        </div>
      </div>

      {/* FAMILLES */}
      <WorkspaceCard
        title={isSimple ? 'Groupes (Familles)' : 'Champs Vivants'}
        subtitle={isSimple ? 'Définissez les groupes qui interagissent dans la simulation' : 'Configuration des tensions sociales'}
        icon={Users}
        accent="emerald"
        darkMode={dm}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => addFamily({ name: `${isSimple ? 'Groupe' : 'Champ'} ${families.length + 1}`, type: 'neutral', isActive: true, isToxic: false, influence: {} })}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-medium rounded-lg transition-colors"
            >
              <Plus size={13} /> {isSimple ? 'Ajouter un groupe' : 'Nouveau champ'}
            </button>
          </div>

          {families.length === 0 && isSimple && (
            <div className={`py-10 text-center rounded-xl border-2 border-dashed ${dm ? 'border-zinc-800 text-zinc-600' : 'border-zinc-200 text-zinc-400'}`}>
              <Users size={24} className="mx-auto mb-2 opacity-40" />
              <p className="text-[12px] mb-1">Aucun groupe créé</p>
              <p className="text-[11px]">Commencez par "Étudiants", "Professeurs", "Administration"</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {families.map((field, index) => (
              <div key={field.id} className={`relative p-4 rounded-xl border group transition-all ${dm ? 'bg-zinc-900/40 border-zinc-800 hover:border-emerald-500/30' : 'bg-white border-zinc-200 hover:border-emerald-300 shadow-sm'}`}>
                <button onClick={() => removeFamily(index)} className={`absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity ${dm ? 'text-zinc-600 hover:text-red-400' : 'text-zinc-300 hover:text-red-500'}`}>
                  <Trash2 size={13} />
                </button>
                <div className="space-y-3">
                  <div>
                    <FieldLabel label={isSimple ? 'Nom du groupe' : 'Field_Label'} darkMode={dm} />
                    <input {...register(`initialState.families.${index}.name`)} placeholder={isSimple ? 'Ex: Professeurs' : 'Field Name'} className={inputCls} />
                  </div>
                  <div>
                    <FieldLabel label="Type" darkMode={dm} />
                    <select {...register(`initialState.families.${index}.type`)} className={inputCls + ' appearance-none'}>
                      <option value="neutral">Neutre</option>
                      <option value="positive">Positif</option>
                      <option value="toxic">Toxique</option>
                      <option value="service">Service</option>
                    </select>
                  </div>
                  <div className="flex gap-4 pt-1">
                    <label className={`flex items-center gap-2 cursor-pointer ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      <input type="checkbox" {...register(`initialState.families.${index}.isActive`)} className="w-3.5 h-3.5 accent-emerald-500 rounded" />
                      <span className="text-[11px]">{isSimple ? 'Actif' : 'Active'}</span>
                    </label>
                    {!isSimple && (
                      <label className="flex items-center gap-2 cursor-pointer text-red-400/70">
                        <input type="checkbox" {...register(`initialState.families.${index}.isToxic`)} className="w-3.5 h-3.5 accent-red-500 rounded" />
                        <span className="text-[11px]">Toxic</span>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </WorkspaceCard>

      {/* AGENTS */}
      <WorkspaceCard
        title={isSimple ? 'Personnages (Agents)' : 'Registre Agents'}
        subtitle={isSimple ? 'Les personnages qui incarnent les groupes' : 'Allocation des entités et cadres épistémologiques'}
        icon={Users}
        accent="indigo"
        darkMode={dm}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => addAgent({ name: '', family: '', role: 'neutre', status: 'neutre', theory: { primaryConcept: '', author: '' }, resources: { capital: 50 } })}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-[12px] font-medium rounded-lg transition-colors"
            >
              <Plus size={13} /> {isSimple ? 'Ajouter un personnage' : 'Initialize_Agent'}
            </button>
          </div>

          {agents.length === 0 && isSimple && (
            <div className={`py-10 text-center rounded-xl border-2 border-dashed ${dm ? 'border-zinc-800 text-zinc-600' : 'border-zinc-200 text-zinc-400'}`}>
              <Users size={24} className="mx-auto mb-2 opacity-40" />
              <p className="text-[12px] mb-1">Aucun personnage créé</p>
              <p className="text-[11px]">Créez au moins 2-3 personnages principaux</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {agents.map((field, index) => (
              <div key={field.id} className={`rounded-xl border p-4 group transition-all ${dm ? 'bg-zinc-900/40 border-zinc-800 hover:border-indigo-500/30' : 'bg-white border-zinc-200 hover:border-indigo-200 shadow-sm'}`}>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-3 flex-1">
                    <span className={`text-[10px] font-mono font-bold ${dm ? 'text-indigo-500' : 'text-indigo-600'}`}>0{index + 1}</span>
                    <input {...register(`initialState.agentTemplates.${index}.name`)} placeholder={isSimple ? 'Nom du personnage' : 'AGENT_NAME'} className={`bg-transparent  text-[13px] font-semibold focus:outline-none w-full py-1 transition-colors ${dm ? 'border-zinc-800 focus:border-indigo-500 text-zinc-200' : 'border-zinc-200 focus:border-indigo-400 text-zinc-900'}`} />
                  </div>
                  <button onClick={() => removeAgent(index)} className={`ml-3 opacity-0 group-hover:opacity-100 transition-opacity ${dm ? 'text-zinc-600 hover:text-red-400' : 'text-zinc-300 hover:text-red-500'}`}>
                    <Trash2 size={14} />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <FieldLabel label={isSimple ? 'Rôle' : 'Status_Ontology'} darkMode={dm} />
                    <select {...register(`initialState.agentTemplates.${index}.status`)} className={inputCls + ' appearance-none'}>
                      <option value="neutre">Neutre</option>
                      <option value="rebel">Résistant</option>
                      <option value="dominant">Dominant</option>
                      <option value="empathic">Empathique</option>
                      {!isSimple && <><option value="influenced">Influencé</option><option value="critical">Critique</option></>}
                    </select>
                  </div>
                  <div>
                    <FieldLabel label={isSimple ? 'Groupe' : 'Field_Family'} darkMode={dm} />
                    <input {...register(`initialState.agentTemplates.${index}.family`)} placeholder={isSimple ? 'Nom du groupe' : 'FAMILY_ID'} className={inputCls} />
                  </div>
                </div>

                {!isSimple && (
                  <div className={`p-3 rounded-lg border mb-3 space-y-2 ${dm ? 'bg-zinc-950/40 border-zinc-800' : 'bg-zinc-50 border-zinc-200'}`}>
                    <div className="flex items-center gap-2 opacity-60">
                      <Database size={11} />
                      <span className="text-[10px] font-bold uppercase tracking-widest">Theory Core</span>
                    </div>
                    <input {...register(`initialState.agentTemplates.${index}.theory.primaryConcept`)} placeholder="Concept" className={inputCls} />
                    <div className="grid grid-cols-3 gap-2">
                      <input {...register(`initialState.agentTemplates.${index}.theory.author`)} placeholder="Author" className={inputCls + ' col-span-2'} />
                      <input type="number" {...register(`initialState.agentTemplates.${index}.theory.year`)} placeholder="Year" className={inputCls} />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <FieldLabel label={isSimple ? 'Ressources' : 'Resource_Metrics'} darkMode={dm} />
                  {Object.keys(watch(`initialState.agentTemplates.${index}.resources`) || {}).map((resourceKey) => (
                    <div key={resourceKey} className={`flex items-center gap-3 p-2 rounded-lg ${dm ? 'bg-zinc-900/40' : 'bg-zinc-50'}`}>
                      <span className={`text-[10px] font-mono w-20 truncate uppercase ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>{resourceKey}</span>
                      <input type="range" {...register(`initialState.agentTemplates.${index}.resources.${resourceKey}`, { valueAsNumber: true })} min="0" max="100" className="flex-1 h-1.5 accent-emerald-500 cursor-pointer" />
                      <span className={`text-[11px] font-mono w-8 text-right ${dm ? 'text-emerald-400' : 'text-emerald-600'}`}>{watch(`initialState.agentTemplates.${index}.resources.${resourceKey}`)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </WorkspaceCard>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROTOCOLE 3 — refonte progressive disclosure
// ─────────────────────────────────────────────────────────────────────────────

export function Protocol3_Sequence({ control, register, watch, darkMode }) {
  const { interfaceMode, ui, toggleStepExpanded } = useCaseCreator();
  const isSimple = interfaceMode === 'simplifie';
  const dm = darkMode;
  const tk = tokens(dm);

  const { fields: steps, append: addStep, remove: removeStep } = useFieldArray({ control, name: 'steps' });
  const { expandedSteps } = ui;

  const etapesParDefaut = [
    { title: 'Initialisation', description: 'Émergence des premières contradictions internes.', problematisation: "Comment l'équilibre tient-il encore ?", epistemologicalQuestion: 'Quelle est la nature de la stabilité perçue ?', phases: [{ type: 'observation', duration: 15, description: 'Lecture des variables' }], actions: [], causality: [], autoEvents: [] },
    { title: 'Montée des Tensions', description: 'Prise de conscience des acteurs sur les rapports de force.', problematisation: 'Quand la conscience devient-elle action ?', epistemologicalQuestion: "Savoir suffit-il à changer la réalité ?", phases: [{ type: 'tension', duration: 20, description: 'Divergence des forces' }], actions: [], causality: [], autoEvents: [] },
    { title: 'Intervention', description: "Tentative coordonnée de rupture avec l'ordre établi.", problematisation: "La rupture peut-elle éviter d'être absorbée ?", epistemologicalQuestion: "Existe-t-il une science de la rupture ?", phases: [{ type: 'action', duration: 30, description: 'Intervention directe' }], actions: [], causality: [], autoEvents: [] },
    { title: 'Point de Rupture', description: 'Bascule irréversible ou retour brutal à l\'ordre ancien.', problematisation: 'Où se situe le seuil de non-retour ?', epistemologicalQuestion: 'La réalité change-t-elle avant ou après la bascule ?', phases: [{ type: 'bifurcation', duration: 10, description: 'Trajectoires critiques' }], actions: [], causality: [], autoEvents: [] },
    { title: 'Instabilité Critique', description: "Dernier sursaut ou accélération de l'effondrement.", problematisation: 'La fin est-elle évitable ?', epistemologicalQuestion: 'Le temps est-il une ligne ou un cycle ?', phases: [{ type: 'tension', duration: 15, description: 'Haute instabilité' }], actions: [], causality: [], autoEvents: [] },
    { title: 'Résolution', description: 'Conséquences de la bascule : nouvel ordre ou chaos.', problematisation: 'Quelle forme prend la nouvelle stabilité ?', epistemologicalQuestion: 'Le nouveau est-il vraiment différent ?', phases: [{ type: 'consequences', duration: 25, description: 'Projection des impacts' }], actions: [], causality: [], autoEvents: [] },
    { title: 'Bilan Système', description: 'Analyse finale des leçons et des mécanismes révélés.', problematisation: 'Que reste-t-il après la résolution ?', epistemologicalQuestion: 'Quelle connaissance survit au système ?', phases: [{ type: 'observation', duration: 20, description: 'Analyse finale' }], actions: [], causality: [], autoEvents: [] },
  ];

  return (
    <div className="space-y-5 animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? 'text-white' : 'text-zinc-900'}`}>
            {isSimple ? 'Séquence temporelle' : 'Séquence Narrative'}
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            {isSimple ? 'La progression des événements dans votre simulation' : 'Segmentation dialectique — minimum 7 segments requis'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => addStep({ title: `Segment ${steps.length + 1}`, description: '', problematisation: '', epistemologicalQuestion: '', phases: [], actions: [], causality: [], autoEvents: [], narrativeOverride: '', feedback: '' })}
          className="flex items-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white text-[12px] font-medium rounded-lg transition-colors"
        >
          <Plus size={14} /> {isSimple ? 'Ajouter une étape' : 'Add_Segment'}
        </button>
      </div>

      {/* Barre de validation */}
      <div className={`flex items-center justify-between p-3 rounded-xl border text-[12px] font-medium ${
        steps.length < 7
          ? dm ? 'bg-red-500/5 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-600'
          : dm ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-600'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full animate-pulse ${steps.length < 7 ? 'bg-red-500' : 'bg-emerald-500'}`} />
          {steps.length < 7
            ? `${steps.length}/7 segments — Il manque ${7 - steps.length} segment(s) pour valider`
            : `Séquence validée — ${steps.length} segments`}
        </div>
        {steps.length < 7 && (
          <button
            type="button"
            onClick={() => { for (let i = steps.length; i < 7; i++) { addStep(etapesParDefaut[i]); } }}
            className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors ${dm ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
          >
            Injecter les 7 segments par défaut
          </button>
        )}
      </div>

      {/* Timeline */}
      {steps.length === 0 && (
        <div className={`py-16 rounded-xl border-2 border-dashed text-center ${dm ? 'border-zinc-800' : 'border-zinc-200'}`}>
          <GitBranch size={28} className="mx-auto mb-3 opacity-30" />
          <p className={`text-[13px] font-medium mb-1 ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>Aucun segment créé</p>
          <p className={`text-[11px] ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>Ajoutez des étapes ou injectez la séquence par défaut</p>
        </div>
      )}

      <div className="relative border-l border-white/10 ml-6 pl-10 space-y-6">
        {steps.map((step, index) => (
          <StepEditor
            key={step.id}
            index={index}
            register={register}
            control={control}
            removeStep={removeStep}
            stepsCount={steps.length}
            expandedSteps={expandedSteps}
            toggleExpanded={toggleStepExpanded}
            darkMode={dm}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROTOCOLE 4 — refonte progressive disclosure
// ─────────────────────────────────────────────────────────────────────────────

export function Protocol4_Engine({ control, register, watch, darkMode }) {
  const { interfaceMode } = useCaseCreator();
  const isSimple = interfaceMode === 'simplifie';
  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = `${tk.input} border outline-none transition-all rounded-lg px-3 py-2 text-[12px] w-full`;

  const causeOptions = watch('initialState.families')?.map(f => f.name).filter(Boolean) || [];
  const effectOptions = ['bienEtre', 'equite', 'creativite', 'stabilite', 'apprentissage', 'sante', 'mediation'];

  return (
    <div className="space-y-5 animate-in fade-in duration-500">
      <div className="mb-6">
        <h2 className={`text-2xl font-bold tracking-tight ${dm ? 'text-white' : 'text-zinc-900'}`}>
          {isSimple ? 'Moteur de simulation' : 'Engine_Room'}
        </h2>
        <p className={`text-[12px] mt-1 ${tk.textSub}`}>
          {isSimple ? 'Calibrez comment les variables évoluent au fil du temps' : 'Engine Calibration Core — Inference & Evolutionary Systems'}
        </p>
      </div>

      {/* Modules engine */}
      <WorkspaceCard title="Moteur d'inférence" subtitle="Prédiction et modélisation des trajectoires" icon={BrainCircuit} accent="indigo" darkMode={dm} defaultOpen>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Inference Engine */}
          <div className={`p-5 rounded-xl border ${dm ? 'bg-indigo-950/10 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
            <div className={`flex items-center gap-2 mb-5 ${dm ? 'text-indigo-400' : 'text-indigo-600'}`}>
              <BrainCircuit size={15} />
              <span className="text-[11px] font-bold uppercase tracking-wider">Inference_Engine</span>
            </div>
            <div className="space-y-4">
              <label className={`flex items-center justify-between cursor-pointer ${dm ? 'text-zinc-300' : 'text-zinc-700'}`}>
                <span className="text-[12px]">Mode prédictif</span>
                <input type="checkbox" {...register('simulationEngine.predictionModel.enabled')} className="w-4 h-4 accent-indigo-500 rounded" />
              </label>
              <div>
                <FieldLabel label="Méthodologie" darkMode={dm} />
                <select {...register('simulationEngine.predictionModel.method')} className={inputCls + ' appearance-none'}>
                  <option value="bayesian">Bayesian Inference</option>
                  <option value="markov">Markov Chains</option>
                  <option value="mlp">Multilayer Perceptron</option>
                  <option value="regression">Linear Regression</option>
                </select>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <FieldLabel label="Horizon" darkMode={dm} />
                  <span className={`text-[11px] font-mono ${dm ? 'text-indigo-400' : 'text-indigo-600'}`}>{watch('simulationEngine.predictionModel.horizon') || 15} Ticks</span>
                </div>
                <input type="range" min="5" max="100" step="5" {...register('simulationEngine.predictionModel.horizon', { valueAsNumber: true })} className="w-full h-1.5 accent-indigo-500 cursor-pointer" />
              </div>
              <div className="space-y-2 pt-2">
                {[{ key: 'storeHistory', label: 'Stocker l\'historique' }, { key: 'counterfactuals', label: 'Counterfactuals' }].map(opt => (
                  <label key={opt.key} className={`flex items-center gap-2 cursor-pointer text-[11px] ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <input type="checkbox" {...register(`simulationEngine.predictionModel.${opt.key}`)} className="w-3.5 h-3.5 accent-indigo-500 rounded" />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Evolutionary Core */}
          <div className={`p-5 rounded-xl border ${dm ? 'bg-purple-950/10 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
            <div className={`flex items-center gap-2 mb-5 ${dm ? 'text-purple-400' : 'text-purple-600'}`}>
              <Zap size={15} />
              <span className="text-[11px] font-bold uppercase tracking-wider">Evolutionary_Core</span>
            </div>
            <div className="space-y-4">
              <label className={`flex items-center justify-between cursor-pointer ${dm ? 'text-zinc-300' : 'text-zinc-700'}`}>
                <span className="text-[12px]">Auto-calibration</span>
                <input type="checkbox" {...register('simulationEngine.calibration.enabled')} className="w-4 h-4 accent-purple-500 rounded" />
              </label>
              <div>
                <FieldLabel label="Algorithme" darkMode={dm} />
                <select {...register('simulationEngine.calibration.mode')} className={inputCls + ' appearance-none'}>
                  <option value="genetic">Genetic Heuristics</option>
                  <option value="gradient">Gradient Descent</option>
                  <option value="bayesian-optimization">Bayesian Optimization</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel label="Population" darkMode={dm} />
                  <input type="number" {...register('simulationEngine.calibration.populationSize', { valueAsNumber: true })} className={inputCls} />
                </div>
                <div>
                  <FieldLabel label="Générations" darkMode={dm} />
                  <input type="number" {...register('simulationEngine.calibration.generations', { valueAsNumber: true })} className={inputCls} />
                </div>
              </div>
              <p className={`text-[10px] italic ${tk.textMuted}`}>Taux mutation : {watch('simulationEngine.calibration.mutationRate') || 0.12}</p>
            </div>
          </div>

          {/* Excavation */}
          <div className={`p-5 rounded-xl border ${dm ? 'bg-zinc-900/40 border-zinc-700' : 'bg-zinc-50 border-zinc-200'}`}>
            <div className={`flex items-center gap-2 mb-5 ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>
              <Database size={15} />
              <span className="text-[11px] font-bold uppercase tracking-wider">Excavation_Module</span>
            </div>
            <div className="space-y-4">
              <label className={`flex items-center justify-between cursor-pointer ${dm ? 'text-zinc-300' : 'text-zinc-700'}`}>
                <span className="text-[12px]">Logs détaillés</span>
                <input type="checkbox" {...register('simulationEngine.logs.store')} className="w-4 h-4 accent-zinc-500 rounded" />
              </label>
              <div>
                <FieldLabel label="Granularité" darkMode={dm} />
                <select {...register('simulationEngine.logs.granularity')} className={inputCls + ' appearance-none'}>
                  <option value="tick">Tick</option>
                  <option value="step">Step</option>
                  <option value="action">Action</option>
                </select>
              </div>
              <div>
                <FieldLabel label="Discovery Engine" darkMode={dm} />
                <select {...register('simulationEngine.processMining.discoveryAlgorithm')} className={inputCls + ' appearance-none'}>
                  <option value="heuristic">Heuristic Miner</option>
                  <option value="inductive">Inductive Miner</option>
                  <option value="alpha">Alpha Algorithm</option>
                </select>
              </div>
              <div className="space-y-2">
                {[{ key: 'conformanceChecking', label: 'Conformance Checking' }, { key: 'bottleneckDetection', label: 'Bottleneck Detection' }].map(opt => (
                  <label key={opt.key} className={`flex items-center gap-2 cursor-pointer text-[11px] ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <input type="checkbox" {...register(`simulationEngine.processMining.${opt.key}`)} className="w-3.5 h-3.5 accent-zinc-500 rounded" />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </WorkspaceCard>

      {/* Dynamics */}
      <WorkspaceCard title="Paramètres dynamiques" subtitle="Formules d'évolution pour chaque variable du système" icon={Activity} accent="emerald" darkMode={dm} defaultOpen={false}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {effectOptions.map(param => (
            <div key={param} className={`p-3 rounded-lg border ${dm ? 'bg-zinc-900/40 border-zinc-800' : 'bg-zinc-50 border-zinc-200'}`}>
              <FieldLabel label={param} darkMode={dm} />
              <select {...register(`simulationEngine.dynamics.${param}.formula`)} className={inputCls + ' appearance-none text-[11px]'}>
                <option value="pesa_nonlinear_threshold">Nonlinear Threshold</option>
                <option value="pesa_basic_linear">Basic Linear</option>
              </select>
            </div>
          ))}
        </div>
      </WorkspaceCard>

      {/* Matrice causale */}
      <WorkspaceCard title="Liens causaux & métriques" subtitle="Relations cause-effet entre familles et paramètres système" icon={Network} accent="purple" darkMode={dm} defaultOpen={false}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <FieldLabel label="Liens causaux" hint="Structure narrative des causalités" darkMode={dm} />
              <span className={`text-[10px] font-mono ${tk.textMuted}`}>{causeOptions.length} familles</span>
            </div>
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
                  <select {...register(`simulationEngine.causality.${i}.cause`)} className={inputCls + ' appearance-none text-[11px]'}>
                    <option value="">Source (Cause)...</option>
                    {causeOptions.map(name => (<option key={name} value={name}>{name}</option>))}
                  </select>
                  <div className={`text-lg font-bold ${watch(`simulationEngine.causality.${i}.weight`) < 0 ? 'text-red-500' : dm ? 'text-indigo-400' : 'text-indigo-600'}`}>
                    {watch(`simulationEngine.causality.${i}.weight`) < 0 ? '⊣' : '→'}
                  </div>
                  <select {...register(`simulationEngine.causality.${i}.effect`)} className={inputCls + ' appearance-none text-[11px]'}>
                    <option value="">Impact (Effect)...</option>
                    {effectOptions.map(param => (<option key={param} value={param}>{param}</option>))}
                  </select>
                  <input type="hidden" {...register(`simulationEngine.causality.${i}.weight`)} />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <FieldLabel label="Métriques calculées" hint="Variables dérivées du moteur" darkMode={dm} />
            {Object.entries(watch('simulationEngine.computedMetrics') || {}).map(([key, label]) => (
              <div key={key} className={`p-3 rounded-lg border flex justify-between items-center ${dm ? 'bg-zinc-900/40 border-zinc-800' : 'bg-white border-zinc-200'}`}>
                <div>
                  <div className={`text-[10px] font-mono ${tk.textMuted}`}>{key}</div>
                  <div className={`text-[13px] font-medium ${dm ? 'text-zinc-200' : 'text-zinc-800'}`}>{label}</div>
                </div>
                <span className={`text-[10px] font-medium px-2 py-1 rounded-md ${dm ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>Active</span>
                <input type="hidden" {...register(`simulationEngine.computedMetrics.${key}`)} />
              </div>
            ))}
            {Object.keys(watch('simulationEngine.computedMetrics') || {}).length === 0 && (
              <div className={`p-8 rounded-xl border-2 border-dashed text-center ${dm ? 'border-zinc-800 text-zinc-600' : 'border-zinc-200 text-zinc-400'}`}>
                <p className="text-[11px]">Aucune métrique calculée définie</p>
              </div>
            )}
          </div>
        </div>
      </WorkspaceCard>

      {/* Status */}
      <div className={`flex items-center gap-3 p-4 rounded-xl border ${watch('simulationEngine.predictionModel.enabled') ? dm ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-700' : dm ? 'bg-zinc-900/40 border-zinc-700 text-zinc-500' : 'bg-zinc-50 border-zinc-200 text-zinc-500'}`}>
        <div className={`w-2 h-2 rounded-full ${watch('simulationEngine.predictionModel.enabled') ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-500'}`} />
        <span className="text-[12px] font-medium">Moteur : {watch('simulationEngine.predictionModel.enabled') ? 'Actif' : 'En veille'}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROTOCOLE 5 — refonte progressive disclosure (code métier intact)
// ─────────────────────────────────────────────────────────────────────────────

export function Protocol5_Solutions({ register, watch, darkMode }) {
  const { interfaceMode, ui, generateSolutions } = useCaseCreator();
  const isSimple = interfaceMode === 'simplifie';
  const dm = darkMode;
  const tk = tokens(dm);

  const [selectedTone, setSelectedTone] = useState('');
  const [solutionPrompt, setSolutionPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const solutions = watch('solutions') || [];

  const handleGenerate = async () => {
    if (!selectedTone && !solutionPrompt.trim()) return;
    setIsGenerating(true);
    const context = {
      stepsCount: watch('steps')?.length || 0,
      families: watch('initialState.families') || [],
      parameters: watch('initialState.parameters') || {}
    };
    await generateSolutions(selectedTone, solutionPrompt, context);
    setIsGenerating(false);
  };

  const toneOptions = isSimple
    ? [{ value: 'reformiste', label: 'Réformiste (changement progressif)' }, { value: 'radicale', label: 'Radicale (transformation profonde)' }]
    : [{ value: 'tragique', label: 'TRAGIQUE (Inéluctabilité)' }, { value: 'cynique', label: 'CYNIQUE (Lucidité froide)' }, { value: 'reformiste', label: 'RÉFORMISTE (Équilibre)' }, { value: 'radicale', label: 'RADICALE (Subversion)' }, { value: 'chaotique', label: 'CHAOTIQUE (Dissolution)' }];

  return (
    <div className="space-y-5 animate-in fade-in duration-500">
      <div className="mb-6">
        <h2 className={`text-2xl font-bold tracking-tight ${dm ? 'text-white' : 'text-zinc-900'}`}>
          {isSimple ? 'Issues de la simulation' : 'Bifurcations finales'}
        </h2>
        <p className={`text-[12px] mt-1 ${tk.textSub}`}>
          {isSimple ? 'Générez les résultats possibles et le débrief pédagogique' : 'Résolution narrative et projection des vecteurs de sortie'}
        </p>
      </div>

      {/* Génération */}
      <WorkspaceCard title="Générer les résultats" subtitle="Choisissez un ton narratif et précisez l'intention" icon={Sparkles} accent="purple" required darkMode={dm} defaultOpen>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <FieldLabel label={isSimple ? 'Type de résultat' : 'Archétype narratif'} required darkMode={dm} />
            <div className="relative">
              <select value={selectedTone} onChange={(e) => setSelectedTone(e.target.value)} className={`${tk.input} border outline-none transition-all rounded-lg px-3 py-2.5 text-[13px] w-full appearance-none`}>
                <option value="">{isSimple ? '-- Choisir un type --' : '-- Sélectionner un vecteur --'}</option>
                {toneOptions.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
              </select>
              <ChevronDown size={13} className={`absolute right-3 inset-y-0 my-auto pointer-events-none ${dm ? 'text-zinc-500' : 'text-zinc-400'}`} />
            </div>
            {isSimple && <p className={`text-[11px] mt-1.5 italic ${tk.textMuted}`}>Choisissez le type de résultat à explorer</p>}
          </div>
          <div>
            <FieldLabel label={isSimple ? 'Précisions (optionnel)' : "Précision d'intention"} darkMode={dm} />
            <textarea
              value={solutionPrompt}
              onChange={(e) => setSolutionPrompt(e.target.value)}
              rows={3}
              placeholder={isSimple ? 'Décrivez ce que vous voulez voir...' : "Spécifiez la singularité de cette fin..."}
              className={`${tk.input} border outline-none transition-all rounded-lg px-3 py-2.5 text-[13px] w-full resize-none`}
            />
          </div>
        </div>
        <div className="mt-5 pt-4  flex justify-between items-center">
          <span className={`text-[11px] font-medium ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>
            {isGenerating ? 'Génération en cours...' : 'Prêt à générer'}
          </span>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating || (!selectedTone && !solutionPrompt.trim())}
            className="flex items-center gap-2 px-6 py-2.5 bg-purple-500 hover:bg-purple-600 text-white text-[12px] font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
          >
            {isGenerating ? <><Loader2 size={14} className="animate-spin" />{isSimple ? 'Génération...' : 'Processing...'}</> : <><Sparkles size={14} />{isSimple ? 'Générer les résultats' : 'Projeter les bifurcations'}</>}
          </button>
        </div>
      </WorkspaceCard>

      {/* Résultats */}
      {solutions.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className={`flex-1 h-px ${dm ? 'bg-zinc-800' : 'bg-zinc-200'}`} />
            <span className={`text-[11px] font-semibold uppercase tracking-widest ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>{isSimple ? 'Résultats générés' : 'Solutions projetées'}</span>
            <div className={`flex-1 h-px ${dm ? 'bg-zinc-800' : 'bg-zinc-200'}`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {solutions.map((sol, index) => (
              <div key={index} className={`rounded-xl border-l-4 border-purple-500 p-5 space-y-3 transition-all ${dm ? 'bg-zinc-900/40    -white/5 -white/5 -white/5 hover:bg-zinc-900/60' : 'bg-white    border-zinc-200 shadow-sm hover:shadow-md'}`}>
                <div className="flex justify-between items-start">
                  <h4 className={`text-[14px] font-bold tracking-tight ${dm ? 'text-white' : 'text-zinc-900'}`}>{sol.name}</h4>
                  <span className={`text-[10px] font-medium px-2 py-1 rounded-md ${dm ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-purple-50 text-purple-600 border border-purple-200'}`}>{sol.interpretation?.tone || sol.difficulty || '—'}</span>
                </div>
                <p className={`text-[12px] leading-relaxed italic ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>"{sol.interpretation?.shortSummary || sol.description}"</p>

                {!isSimple && sol.interpretation && (
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    {[{ label: 'Probabilité réelle', val: sol.interpretation.realWorldProbability }, { label: 'Promesse théorique', val: sol.interpretation.theoreticalPromise }].map(({ label, val }) => (
                      <div key={label} className="space-y-1">
                        <div className="flex justify-between text-[10px]">
                          <span className={tk.textMuted}>{label}</span>
                          <span className={`font-semibold ${dm ? 'text-purple-400' : 'text-purple-600'}`}>{Math.round((val || 0) * 100)}%</span>
                        </div>
                        <div className={`h-1.5 rounded-full overflow-y-auto no-scrollbar ${dm ? 'bg-zinc-800' : 'bg-zinc-200'}`}>
                          <div className="h-full bg-purple-500 rounded-full" style={{ width: `${(val || 0) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isSimple && sol.interpretation?.pedagogicalMessage && (
                  <div className={`pt-2  text-[11px] italic ${dm ? 'border-white/5 text-purple-300/80' : 'border-zinc-100 text-purple-700'}`}>
                    💡 {sol.interpretation.pedagogicalMessage}
                  </div>
                )}

                {!isSimple && sol.requiredActions?.length > 0 && (
                  <div className="pt-2">
                    <p className={`text-[10px] uppercase mb-1.5 ${tk.textMuted}`}>Actions requises :</p>
                    <div className="flex flex-wrap gap-1">
                      {sol.requiredActions.slice(0, 3).map((action, i) => (
                        <span key={i} className={`text-[10px] px-2 py-0.5 rounded border ${dm ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-purple-50 text-purple-600 border-purple-200'}`}>{action}</span>
                      ))}
                      {sol.requiredActions.length > 3 && <span className={`text-[10px] ${dm ? 'text-purple-400/50' : 'text-purple-400'}`}>+{sol.requiredActions.length - 3}</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {!isSimple && (
            <div className={`p-6 rounded-xl border ${dm ? 'bg-zinc-900/40 border-purple-500/20' : 'bg-purple-50/50 border-purple-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Target size={14} className="text-purple-500" />
                <h3 className={`text-[12px] font-bold uppercase tracking-widest ${dm ? 'text-purple-400' : 'text-purple-700'}`}>Synthèse Ontologique Terminale</h3>
              </div>
              <p className={`text-[12px] leading-relaxed ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>
                Les vecteurs de sortie confirment une tension irréversible. La probabilité de succès des voies émancipatrices reste faible (~12–28 %), tandis que les scénarios de reproduction ou d'effondrement dominent.
              </p>
              <p className={`text-[11px] mt-2 italic ${tk.textMuted}`}>Analyse finale : la subversion nécessite une coordination rare des forces actives avant le point de bascule.</p>
            </div>
          )}
        </div>
      )}

      {!solutions.length && (
        <div className={`py-20 flex flex-col items-center justify-center text-center ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>
          <Sparkles size={28} className="mb-3 opacity-30" />
          <p className="text-[13px] font-medium mb-1">{isSimple ? 'Aucun résultat généré' : 'Aucune bifurcation projetée'}</p>
          <p className="text-[11px]">{isSimple ? 'Sélectionnez un type et cliquez sur Générer' : 'Configurez le vecteur narratif ci-dessus'}</p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL : CaseCreator
// ─────────────────────────────────────────────────────────────────────────────

export default function CaseCreator() {
  const navigate = useNavigate();
  const [localDarkMode, setLocalDarkMode] = useState(true);

  const { formData, interfaceMode, ui, updateFormData, loadTemplate, deploySimulation, setProtocol, nextProtocol, prevProtocol, toggleNav, toggleMode } = useCaseCreator();

  const form = useForm({ resolver: zodResolver(genericCaseSchema), defaultValues: formData, mode: 'onChange' });
  const { register, control, handleSubmit, watch, setValue, reset, formState: { isValid, errors } } = form;

  useEffect(() => { reset(formData); }, [formData, reset]);

  useEffect(() => {
    const subscription = watch((value) => {
      Object.keys(value).forEach(key => {
        if (JSON.stringify(value[key]) !== JSON.stringify(formData[key])) {
          updateFormData(key, value[key]);
        }
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, formData, updateFormData]);

  useEffect(() => {
    console.log('=== État validation Zod ===');
    console.log('isValid :', isValid);
    if (Object.keys(errors).length > 0) console.table(Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, { message: v?.message, type: v?.type }])));
  }, [isValid, errors]);

  const onSubmit = async () => { await deploySimulation(navigate); };
  const handleLoadTemplate = (key) => {
    const template = templates[key];
    if (template) { loadTemplate(template); reset(template); }
    else toast.error('Template introuvable');
  };

  const ProtocolComponents = { 1: Protocol1_Ontologie, 2: Protocol2_Structure, 3: Protocol3_Sequence, 4: Protocol4_Engine, 5: Protocol5_Solutions };
  const CurrentProtocol = ProtocolComponents[ui.currentProtocol];
  const currentP = PROTOCOLS.find(p => p.id === ui.currentProtocol);
  const dm = localDarkMode;
  const tk = tokens(dm);
  const isSimple = interfaceMode === 'simplifie'; 

  return (
    <div className={`h-screen flex flex-col ${dm ? 'bg-zinc-950' : 'bg-zinc-50'}`}>

      {/* ════ HEADER — aligné sur ParcoursCreator ════ */}
      <header className={`sticky top-0 z-50 px-5 py-3  flex justify-between items-center backdrop-blur-xl ${dm ? 'bg-zinc-950/90 border-white/8' : 'bg-white/90 border-zinc-200 shadow-sm'}`}>

        {/* Gauche */}
        <div className="flex items-center gap-4">
          <button
            onClick={toggleNav}
            className={`p-2 rounded-lg transition-all ${ui.isNavOpen ? dm ? 'bg-indigo-500/15 border-indigo-500/30 text-indigo-300' : 'bg-indigo-50 border-indigo-200 text-indigo-600' : dm ? 'border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}
            title="Afficher/masquer le panneau de navigation"
          >
            {ui.isNavOpen ? <X size={16} /> : <Menu size={16} />}
          </button>

          <div className="flex items-center gap-2.5">
            <div className={`p-1.5 rounded-lg border ${dm ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
              <Cpu size={16} className="text-indigo-500" />
            </div>
            <div>
              <div className={`text-[13px] font-bold ${dm ? 'text-white' : 'text-zinc-900'}`}>Constructeur de simulation</div>
              <div className={`text-[10px] ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>
                PESACORE — {formData.slug || "Nouveau Cas d'étude"}
              </div>
            </div>
          </div>

          {/* Protocole actif */}
          {currentP && (
            <div className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg border ${dm ? 'border-indigo-500/20 bg-indigo-500/8' : 'border-indigo-200 bg-indigo-50'}`}>
              <currentP.icon size={12} className="text-indigo-500" />
              <span className={`text-[12px] font-semibold ${dm ? 'text-indigo-300' : 'text-indigo-700'}`}>{isSimple ? currentP.labelSimple : currentP.labelExpert}</span>
            </div>
          )}
        </div>

        {/* Centre : Template */}
        <div className="flex-1 max-w-sm mx-8">
          <div className="relative">
            <Layers size={14} className="absolute left-3 inset-y-0 my-auto text-zinc-400 pointer-events-none" />
            <select
              onChange={(e) => e.target.value && handleLoadTemplate(e.target.value)}
              defaultValue=""
              className={`w-full pl-9 pr-8 py-2 rounded-lg border text-[12px] outline-none appearance-none cursor-pointer transition-all ${dm ? 'bg-zinc-900 border-zinc-700 text-zinc-300 focus:border-indigo-400/50' : 'bg-white border-zinc-200 text-zinc-700 focus:border-indigo-300 shadow-sm'}`}
            >
              <option value="" disabled>Charger un modèle...</option>
              <optgroup label="Éducation"><option value="critique-systemique">Pensée Critique</option></optgroup>
              <optgroup label="Santé"><option value="retina-view">Santé Visuelle</option></optgroup>
              <optgroup label="Culture"><option value="art-critique-sociale">Engagement Artistique</option></optgroup>
              <optgroup label="Entreprise"><option value="conflits-internes">Gestion Conflits</option><option value="ass-mat-v2">Ass Mat</option></optgroup>
            </select>
            <ChevronDown size={13} className="absolute right-3 inset-y-0 my-auto text-zinc-400 pointer-events-none" />
          </div>
        </div>

        {/* Droite */}
        <div className="flex items-center gap-2">
          {/* Toggle mode simplifié / expert */}
          <button
            onClick={toggleMode}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[11px] font-medium transition-all ${isSimple ? dm ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400' : 'bg-cyan-50 border-cyan-200 text-cyan-700' : dm ? 'bg-purple-500/10 border-purple-500/20 text-purple-400' : 'bg-purple-50 border-purple-200 text-purple-700'}`}
          >
            {isSimple ? '🎓 Simplifié' : '⚡ Expert'}
          </button>

          {/* Toggle dark/light */}
          <button
            onClick={() => setLocalDarkMode(prev => !prev)}
            className={`p-2 rounded-lg border transition-all ${dm ? 'bg-zinc-800 border-zinc-700 text-yellow-400 hover:bg-zinc-700' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
            title={dm ? 'Passer en mode clair' : 'Passer en mode sombre'}
          >
            {dm ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /></svg>
            )}
          </button>

          {/* Fermer */}
          <button
            onClick={() => {
              if (window.history.length > 1) {
                window.history.back();
              } else {
                window.location.href = '/';
              }
            }}
            className={`p-2 rounded-lg border transition-all ${dm ? 'border-zinc-800 text-zinc-500 hover:border-red-500/40 hover:text-red-400 hover:bg-red-500/5' : 'border-zinc-200 text-zinc-500 hover:border-red-300 hover:text-red-500 hover:bg-red-50'}`}
            title="Fermer"
          >
            <Power size={16} />
          </button>
        </div>
      </header>

      {/* ════ LAYOUT PRINCIPAL ════ */}
      <div className="flex-1 flex overflow-y-auto no-scrollbar">

        {/* Sidebar */}
        {ui.isNavOpen && (
          <aside className={`flex-shrink-0  overflow-y-auto no-scrollbar transition-all duration-200 ${dm ? 'border-white/6' : 'border-zinc-200'}`} style={{ width: 288 }}>
            <MissionControl
              protocols={PROTOCOLS}
              currentProtocol={ui.currentProtocol}
              setProtocol={setProtocol}
              darkMode={dm}
              interfaceMode={interfaceMode}
            />
          </aside>
        )}

        {/* Workspace */}
        <main className={`flex-1 flex flex-col overflow-y-auto no-scrollbar ${dm ? 'bg-zinc-950' : 'bg-zinc-50'}`}>

          {/* Barre de contexte */}
          <WorkspaceContextBar
            protocolLabel={isSimple ? currentP?.labelSimple || '' : currentP?.labelExpert || ''}
            protocolHint={currentP?.hint}
            darkMode={dm}
          />

          {/* Nav protocoles horizontale — identique ParcoursCreator */}
          <div className={`flex items-center gap-0.5 px-6 py-2  ${dm ? 'border-white/5 bg-zinc-950' : 'border-zinc-200 bg-white'}`}>
            {PROTOCOLS.map((p) => {
              const isActive = ui.currentProtocol === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setProtocol(p.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-medium transition-all ${
                    isActive
                      ? dm ? 'bg-indigo-500/12 text-indigo-300 border border-indigo-500/20' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                      : dm ? 'text-zinc-500 hover:text-zinc-300 border border-transparent' : 'text-zinc-500 hover:text-zinc-700 border border-transparent hover:bg-zinc-100'
                  }`}
                >
                  <p.icon size={13} className={isActive ? 'text-indigo-500' : dm ? 'text-zinc-600' : 'text-zinc-400'} />
                  <span>{isSimple ? p.labelSimple : p.labelExpert}</span>
                </button>
              );
            })}
          </div>

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto overflow-x-auto no-scrollbar p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
              {CurrentProtocol && (
                <CurrentProtocol register={register} control={control} watch={watch} setValue={setValue} errors={errors} darkMode={dm} />
              )}

              {/* ── Footer navigation — identique ParcoursCreator ── */}
              <footer className={`mt-16 pt-8  flex items-center justify-between ${dm ? 'border-zinc-800' : 'border-zinc-200'}`}>
                <div className="flex items-center gap-5">
                  <div className={`text-[11px] ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>
                    <span className="block">Progression</span>
                    <span className={`font-bold text-[14px] ${dm ? 'text-indigo-400' : 'text-indigo-600'}`}>{ui.currentProtocol}/5</span>
                  </div>
                  <div className={`text-[11px] ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>
                    <span className="block">Validation</span>
                    <span className={`font-bold text-[13px] ${isValid ? 'text-emerald-500' : 'text-amber-500'}`}>{isValid ? 'Prêt' : 'Incomplet'}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  {ui.currentProtocol > 1 && (
                    <button
                      type="button"
                      onClick={prevProtocol}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg border text-[12px] font-medium transition-all ${dm ? 'border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-100'}`}
                    >
                      <ArrowLeft size={15} /> Précédent
                    </button>
                  )}
                  {ui.currentProtocol < 5 ? (
                    <button
                      type="button"
                      onClick={nextProtocol}
                      className="flex items-center gap-2 px-8 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white text-[12px] font-semibold rounded-lg transition-colors shadow-sm"
                    >
                      Suivant <ArrowRight size={15} />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit(onSubmit)}
                      disabled={!isValid || ui.isSaving}
                      className="flex items-center gap-2 px-8 py-2.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 text-white text-[12px] font-semibold rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                    >
                      {ui.isSaving ? <><Loader2 size={14} className="animate-spin" /> Déploiement...</> : <><Zap size={14} /> Déployer la simulation</>}
                    </button>
                  )}
                </div>
              </footer>
            </div>
          </div>
        </main>
      </div>

      {/* Erreurs de validation */}
      {!isValid && Object.keys(errors).length > 0 && (
        <div className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl p-4 shadow-2xl backdrop-blur-md animate-in fade-in slide-in-from-bottom-4 ${dm ? 'bg-red-950/90 border border-red-600/40 text-red-200' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
            <span className={`text-[11px] font-bold uppercase tracking-wide ${dm ? 'text-red-400' : 'text-red-600'}`}>
              {Object.keys(errors).length} erreur(s) bloquante(s)
            </span>
          </div>
          <ul className="space-y-1.5 max-h-48 overflow-y-auto text-[11px]">
            {Object.entries(errors).map(([path, error]) => (
              <li key={path} className="flex flex-col">
                <span className={`font-semibold ${dm ? 'text-red-300' : 'text-red-700'}`}>{path}</span>
                <span>{error?.message || 'Champ invalide'}</span>
              </li>
            ))}
          </ul>
          <p className={`text-[10px] mt-2 italic ${dm ? 'opacity-60' : 'opacity-50'}`}>Corrigez ces champs pour activer le déploiement</p>
        </div>
      )}
    </div>
  );
}